import { supabase } from "../../supabase/supabase";
import { apiServerHeaders } from "@/lib/apiServerHeaders";
import {
  LATENCY_CALL_LIMIT,
  LATENCY_WINDOW_DAYS,
  type ProbeResponse,
  type ProbeRow,
} from "./latencyUtils";

export * from "./latencyUtils";

const API_BASE_URL = import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";

// call_logs.agent_server_region and latency_probes are not in the generated types yet.
const db = supabase as any;

const windowStart = () =>
  new Date(Date.now() - LATENCY_WINDOW_DAYS * 24 * 3600 * 1000).toISOString();

// The metrics of the assistant's last calls from this region, newest first.
export async function fetchRecentCallMetrics(
  assistantId: string,
  region: string,
): Promise<unknown[]> {
  const { data, error } = await db
    .from("call_logs")
    .select("id, created_at, metrics")
    .eq("assistant_id", assistantId)
    .eq("agent_server_region", region)
    .gte("created_at", windowStart())
    .order("created_at", { ascending: false })
    .limit(LATENCY_CALL_LIMIT);
  if (error) throw error;
  return (data ?? []).map((row: { metrics: unknown }) => row.metrics);
}

// The newest probe for this assistant and region within the window, or null.
export async function fetchLatestProbe(
  assistantId: string,
  region: string,
): Promise<ProbeRow | null> {
  const { data, error } = await db
    .from("latency_probes")
    .select("*")
    .eq("assistant_id", assistantId)
    .eq("region", region)
    .gte("created_at", windowStart())
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

// Texts for the codes worth rewording; any other code shows the body's `detail`.
const PROBE_ERROR_TEXT: Record<string, string> = {
  probe_not_configured: "Latency probe is not set up on this environment",
  unknown_region: "No agent server has run in this region",
};

// Thrown by runLatencyProbe. The API server's error body is {detail, code, retryable, request_id,
// params}: `code` tells the cases apart, `detail` is the server's text and the message is what to
// show. `retryAfter` (seconds) is set on a rate limit (HTTP 429).
export class LatencyProbeError extends Error {
  constructor(
    public detail: string,
    public status: number,
    public code?: string,
    public retryAfter?: number,
  ) {
    super((code && PROBE_ERROR_TEXT[code]) || detail);
  }
}

// Runs a live probe in the region with the form's current values (`overrides`: assistants
// column names). Returns the response body; the API server also stores it in latency_probes,
// except when every stage failed (id is null then), so the caller shows the body directly.
export async function runLatencyProbe(
  assistantId: string,
  region: string,
  overrides: Record<string, unknown>,
): Promise<ProbeResponse> {
  const response = await fetch(`${API_BASE_URL}/api/assistants/${assistantId}/latency-probe`, {
    method: "POST",
    headers: await apiServerHeaders(),
    body: JSON.stringify({ region, overrides }),
  });
  const body = await response.json().catch(() => ({}));
  if (response.ok) return body as ProbeResponse;
  const retryAfter = Number(
    body?.params?.retry_after_seconds ?? response.headers.get("Retry-After"),
  );
  throw new LatencyProbeError(
    (typeof body?.detail === "string" && body.detail) || `Probe failed (${response.status})`,
    response.status,
    body?.code,
    Number.isFinite(retryAfter) ? Math.ceil(retryAfter) : undefined,
  );
}
